import { defineStore } from 'pinia';
import axios from 'axios';
import { useRoute } from 'vue-router';
import { arrayToQueryStringParameter, getDefaultPagination } from '@/utils/utils';
import { statsCache } from '@/stores/statsCache.ts';
import { parseJsonToMapStrict } from '@/utils.js';
const route = useRoute();
export const useArticlesStore = defineStore('articles', {
    state: () => ({
        items: [],
        pagination: getDefaultPagination(),
        groups: [],
        stats: {
            articleGroup: {},
            articleGroupName: {},
            material: {},
            shape: {},
        },
        order: {},
        details: {},
        isLoadingStats: false,
        statsError: null,
        isLoadingItems: false,
        itemsError: null,
        isLoadingGroups: false,
        groupsError: null,
        isLoadingDetails: false,
        detailsError: null,
    }),
    actions: {
        async fetchStats() {
            this.isLoadingStats = true;
            this.statsError = null;
            try {
                const params = {
                    list: 'all',
                    language: settings.languageCode,
                };
                let jsonString;
                const response = await axios.get(`${settings.siteAddress}/app/api/articles.php`, {
                    params,
                    transformResponse: [
                        (data) => {
                            jsonString = data;
                            return JSON.parse(data);
                        },
                    ],
                });
                const responseData = response.data.data;
                const stats = responseData.stats;
                statsCache.setIfDefined(params, stats);
                this.stats = stats;
                for (const [key, value] of parseJsonToMapStrict(jsonString)?.get('data')?.get('stats')) {
                    if (value instanceof Map) {
                        this.order[key] = [...value.keys()];
                    }
                }
                return this.stats;
            }
            catch (e) {
                this.statsError = e;
                throw e;
            }
            finally {
                this.isLoadingStats = false;
            }
        },
        async fetchArticleGroups() {
            this.isLoadingGroups = true;
            this.groupsError = null;
            try {
                const response = await axios.get(`${settings.siteAddress}/app/api/article-groups.php`, {
                    params: {
                        language: settings.languageCode,
                    },
                });
                const responseData = response.data.data;
                const groups = Object.values(responseData.items.reduce((acc, item) => {
                    const { grouping, groupingName, orderMain, orderSub, ...rest } = item;
                    if (!acc[grouping]) {
                        acc[grouping] = {
                            grouping,
                            groupingName,
                            order: orderMain,
                            children: [],
                        };
                    }
                    acc[grouping].children.push({ ...rest, order: orderSub });
                    return acc;
                }, {}));
                groups.sort((a, b) => a.order - b.order);
                groups.forEach((group) => {
                    group.children.sort((a, b) => a.order - b.order);
                });
                this.groups = groups;
                return this.groups;
            }
            catch (e) {
                this.groupsError = e;
                throw e;
            }
            finally {
                this.isLoadingGroups = false;
            }
        },
        async fetchArticleDetails(articleGroup) {
            this.isLoadingDetails = true;
            this.detailsError = null;
            try {
                const response = await axios.get(`${settings.siteAddress}/app/api/article-groups-details.php`, {
                    params: {
                        articleGroup: articleGroup,
                        language: settings.languageCode,
                    },
                });
                const responseData = response.data.data;
                this.details = responseData;
                return this.details;
            }
            catch (e) {
                this.detailsError = e;
                throw e;
            }
            finally {
                this.isLoadingDetails = false;
            }
        },
        async fetchArticlesFromQuery(route) {
            if (!route.query) {
                return null;
            }
            this.isLoadingItems = true;
            this.itemsError = null;
            try {
                const query = route.query;
                let params = {
                    articleGroup: arrayToQueryStringParameter(query.articleGroup),
                    // itemsPerPage: this.itemsPerPage,
                    language: settings.languageCode,
                    startIndex: parseInt(query.startIndex) >= 1 ? parseInt(query.startIndex) : 1,
                    remainders: (query.remainders === '1') ? '1' : '',
                    material: arrayToQueryStringParameter(query.material),
                    shape: arrayToQueryStringParameter(query.shape),
                    grade: arrayToQueryStringParameter(query.grade),
                    innerDiameter: arrayToQueryStringParameter(query.innerDiameter),
                    outerDiameter: arrayToQueryStringParameter(query.outerDiameter),
                    wallThickness: arrayToQueryStringParameter(query.wallThickness),
                    inStock: query.inStock === 'only' ? '1' : '0',
                };
                switch (query.sort) {
                    case '':
                    case null:
                    case undefined:
                        params.sort = 'outerDiameter';
                        break;
                    case '-':
                        params.sort = '-outerDiameter';
                        break;
                    case 'innerDiameter':
                        params.sort = 'innerDiameter';
                        break;
                    case '-innerDiameter':
                        params.sort = '-innerDiameter';
                        break;
                    case 'relevance':
                        params.sort = '';
                        break;
                    case '-relevance':
                        params.sort = '-';
                        break;
                    default:
                        params.sort = query.sort;
                }
                // if (query.query) {
                //   params = {...params, ...{query: query.query}}
                // } else {
                //   params = {...params, ...{list: query.list || 'all'}}
                // }
                if (query.articleGrouping) {
                    params.list = this.groups
                        .find(({ grouping }) => grouping == query.articleGrouping)
                        ?.children.map(({ articleGroup }) => `articleGroup-${articleGroup}`)
                        .join(',');
                }
                const response = await axios.get(`${settings.siteAddress}/app/api/articles.php`, { params });
                const responseData = response.data.data;
                const stats = responseData.stats;
                statsCache.setIfDefined(params, stats);
                this.items = responseData.items;
                this.pagination.itemsPerPage = responseData.itemsPerPage;
                this.pagination.totalItems = responseData.totalItems;
                this.pagination.startIndex = parseInt(responseData.startIndex);
                this.pagination.currentItemCount = responseData.currentItemCount;
                this.pagination.pagingLinkTemplate = responseData.pagingLinkTemplate;
                return this.items;
            }
            catch (e) {
                this.itemsError = e;
                throw e;
            }
            finally {
                this.isLoadingItems = false;
            }
        },
        async fetchArticle(articleNumber) {
            const response = await axios.get(`/rest/shop/v1/articles/${articleNumber}`, {
                params: {
                    language: settings.languageCode,
                },
            });
            return response?.data?.data;
        },
        // async fetchArticlePrice(articleNumber: string,
        //                         queryParameters: { quantity: number } | {
        //                           specifiedLength: number,
        //                           pieces: number
        //                         }) {
        //   const {quantity, specifiedLength, pieces} = queryParameters || {};
        //   const searchParams = new URLSearchParams(pruneObject({
        //     quantity,
        //     specifiedLength,
        //     pieces,
        //   }));
        //   const response = await axios.get(`/rest/shop/v1/articles/${articleNumber}?${searchParams.toString()}`);
        //   return response?.data?.data;
        // },
    },
});
